import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as ExecuteIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  Visibility as ViewIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';

import WorkflowCanvas from '../../components/Workflows/Canvas/WorkflowCanvas';
import NodeConfigDialog from '../../components/Workflows/NodeConfigDialog';
import WorkflowService from '../../services/api/workflow.service';
import {
  Workflow,
  WorkflowDefinition,
  CreateWorkflowRequest,
  WorkflowValidationResult,
  WorkflowNode
} from '../../types/workflow.types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`workflow-tabpanel-${index}`}
      aria-labelledby={`workflow-tab-${index}`}
      {...other}
    >
      {value === index && <div style={{ padding: 24 }}>{children}</div>}
    </div>
  );
}

const WorkflowsPage: React.FC = () => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [, setExecuteDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Form states
  const [newWorkflowName, setNewWorkflowName] = useState('');
  const [newWorkflowDescription, setNewWorkflowDescription] = useState('');
  const [workflowToDelete, setWorkflowToDelete] = useState<Workflow | null>(null);

  // Validation state
  const [, setValidationResult] = useState<WorkflowValidationResult | null>(null);

  // Node selection and configuration state
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [nodeConfigDialogOpen, setNodeConfigDialogOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState<WorkflowNode | null>(null);

  // Save throttling state
  const [lastSaveTime, setLastSaveTime] = useState<number>(0);

  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      const result = await WorkflowService.getUserWorkflows();
      setWorkflows(result.workflows);
      setError(null);
    } catch (err: any) {
      setError('Failed to load workflows: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkflow = async () => {
    try {
      const defaultDefinition: WorkflowDefinition = {
        nodes: [
          {
            id: 'start_1',
            type: 'start',
            position: { x: 100, y: 100 },
            data: {
              title: 'Start',
              config: {},
              isConnectable: true,
            },
          },
        ],
        edges: [],
        variables: [],
      };

      const request: CreateWorkflowRequest = {
        name: newWorkflowName,
        description: newWorkflowDescription,
        definition: defaultDefinition,
      };

      const result = await WorkflowService.createWorkflow(request);
      setWorkflows([...workflows, result.workflow]);
      setSelectedWorkflow(result.workflow);
      setIsEditing(true);
      setCreateDialogOpen(false);
      setNewWorkflowName('');
      setNewWorkflowDescription('');
      setSuccess('Workflow created successfully');
    } catch (err: any) {
      setError('Failed to create workflow: ' + err.message);
    }
  };

  const handleSaveWorkflow = async (definition: WorkflowDefinition) => {
    if (!selectedWorkflow) return;

    // Throttle saves to prevent flooding (minimum 1 second between saves)
    const now = Date.now();
    if (now - lastSaveTime < 1000) {
      console.log('handleSaveWorkflow: Throttled save attempt, skipping');
      return;
    }

    console.log('handleSaveWorkflow: Proceeding with save');
    setLastSaveTime(now);

    try {
      const updatedWorkflow = await WorkflowService.updateWorkflow(selectedWorkflow.id, {
        definition,
      });

      setWorkflows(workflows.map(w => w.id === updatedWorkflow.id ? updatedWorkflow : w));
      setSelectedWorkflow(updatedWorkflow);
      setSuccess('Workflow saved successfully');
    } catch (err: any) {
      setError('Failed to save workflow: ' + err.message);
    }
  };

  const handleValidateWorkflow = async (definition: WorkflowDefinition) => {
    try {
      const validation = await WorkflowService.validateWorkflow(definition);
      setValidationResult(validation);

      if (validation.isValid) {
        setSuccess('Workflow validation passed');
      } else {
        setError(`Workflow validation failed: ${validation.errors.map(e => e.message).join(', ')}`);
      }
    } catch (err: any) {
      setError('Failed to validate workflow: ' + err.message);
    }
  };

  const handleExecuteWorkflow = async (workflow: Workflow) => {
    try {
      const execution = await WorkflowService.executeWorkflow(workflow.id, {});
      setSuccess(`Workflow execution started: ${execution.id}`);
      setExecuteDialogOpen(false);
    } catch (err: any) {
      setError('Failed to execute workflow: ' + err.message);
    }
  };

  const handleDuplicateWorkflow = async (workflow: Workflow) => {
    try {
      const duplicatedWorkflow = await WorkflowService.duplicateWorkflow(
        workflow.id,
        `${workflow.name} (Copy)`
      );
      setWorkflows([...workflows, duplicatedWorkflow]);
      setSuccess('Workflow duplicated successfully');
    } catch (err: any) {
      setError('Failed to duplicate workflow: ' + err.message);
    }
  };

  const handleDeleteWorkflow = async () => {
    if (!workflowToDelete) return;

    try {
      await WorkflowService.deleteWorkflow(workflowToDelete.id);
      setWorkflows(workflows.filter(w => w.id !== workflowToDelete.id));

      if (selectedWorkflow?.id === workflowToDelete.id) {
        setSelectedWorkflow(null);
        setIsEditing(false);
      }

      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
      setSuccess('Workflow deleted successfully');
    } catch (err: any) {
      setError('Failed to delete workflow: ' + err.message);
    }
  };

  const handleWorkflowSelect = (workflow: Workflow) => {
    setSelectedWorkflow(workflow);
    setIsEditing(false);
    setTabValue(1); // Switch to editor tab
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleNodeSelect = (nodeId: string | null, currentNodes?: WorkflowNode[]) => {
    console.log('WorkflowsPage: handleNodeSelect called with nodeId:', nodeId);
    console.log('WorkflowsPage: currentNodes provided:', !!currentNodes, currentNodes ? `(${currentNodes.length} nodes)` : '');
    setSelectedNodeId(nodeId);

    if (nodeId) {
      // Use currentNodes if provided (from WorkflowCanvas), otherwise fall back to saved workflow
      const nodesToSearch = currentNodes || selectedWorkflow?.definition.nodes || [];
      console.log('WorkflowsPage: Searching in', nodesToSearch.length, 'nodes');
      console.log('WorkflowsPage: Node IDs available:', nodesToSearch.map(n => n.id));

      // Find the selected node in the current nodes
      const node = nodesToSearch.find(n => n.id === nodeId);
      console.log('WorkflowsPage: Found node:', node);
      if (node) {
        console.log('WorkflowsPage: Setting selected node and opening dialog');
        setSelectedNode(node);
        setNodeConfigDialogOpen(true);
      } else {
        console.log('WorkflowsPage: Node not found');
      }
    } else {
      console.log('WorkflowsPage: No nodeId provided');
    }
  };

  const handleNodeConfigSave = (updatedNode: WorkflowNode) => {
    if (!selectedWorkflow) return;

    console.log('handleNodeConfigSave: Updating node', updatedNode.id);
    console.log('handleNodeConfigSave: Current workflow nodes:', selectedWorkflow.definition.nodes.map(n => n.id));

    // Check if the node exists in the current workflow definition
    const existingNodeIndex = selectedWorkflow.definition.nodes.findIndex(node => node.id === updatedNode.id);

    let updatedNodes: WorkflowNode[];
    if (existingNodeIndex >= 0) {
      // Node exists, update it
      console.log('handleNodeConfigSave: Node exists, updating');
      updatedNodes = selectedWorkflow.definition.nodes.map(node =>
        node.id === updatedNode.id ? updatedNode : node
      );
    } else {
      // Node doesn't exist in saved workflow, add it
      console.log('handleNodeConfigSave: Node not found in saved workflow, adding');
      updatedNodes = [...selectedWorkflow.definition.nodes, updatedNode];
    }

    // Update the workflow definition
    const updatedDefinition: WorkflowDefinition = {
      ...selectedWorkflow.definition,
      nodes: updatedNodes,
    };

    console.log('handleNodeConfigSave: Updated definition nodes:', updatedDefinition.nodes.map(n => n.id));

    // Save the updated workflow (this will only be called once per config save)
    handleSaveWorkflow(updatedDefinition);
    setSelectedNode(null);
  };

  const handleNodeConfigClose = () => {
    setNodeConfigDialogOpen(false);
    setSelectedNode(null);
    setSelectedNodeId(null);
  };

  return (
    <Container maxWidth="xl" style={{ paddingTop: 16, paddingBottom: 16, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ borderBottom: '1px solid rgba(0,0,0,0.12)', marginBottom: 16 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Workflow Library" />
          <Tab label="Workflow Editor" disabled={!selectedWorkflow} />
          <Tab label="Execution Monitor" />
        </Tabs>
      </div>

      {/* Workflow Library Tab */}
      <TabPanel value={tabValue} index={0}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Tooltip title="Back to Chat">
              <IconButton
                onClick={() => navigate('/chat')}
                style={{ marginRight: 8 }}
              >
                <BackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h4" component="h1">
              Workflow Automation
            </Typography>
          </div>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            style={{ minWidth: 160 }}
          >
            Create Workflow
          </Button>
        </div>

        {loading ? (
          <Typography>Loading workflows...</Typography>
        ) : workflows.length === 0 ? (
          <Paper style={{ padding: 32, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No workflows yet
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Create your first workflow to get started with automation
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create First Workflow
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {workflows.map((workflow) => (
              <Grid item xs={12} sm={6} md={4} key={workflow.id}>
                <Paper
                  style={{
                    padding: 16,
                    cursor: 'pointer',
                    border: selectedWorkflow?.id === workflow.id ? '2px solid #1976d2' : 'none',
                  }}
                  onClick={() => handleWorkflowSelect(workflow)}
                >
                  <Typography variant="h6" gutterBottom>
                    {workflow.name}
                  </Typography>
                  {workflow.description && (
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {workflow.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {workflow.definition.nodes.length} nodes • {workflow.definition.edges.length} connections
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Updated: {new Date(workflow.updatedAt).toLocaleDateString()}
                  </Typography>

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                    <div>
                      <Tooltip title="Edit Workflow">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedWorkflow(workflow);
                            setIsEditing(true);
                            setTabValue(1);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate Workflow">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateWorkflow(workflow);
                          }}
                        >
                          <DuplicateIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                    <div>
                      <Tooltip title="Execute Workflow">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExecuteWorkflow(workflow);
                          }}
                        >
                          <ExecuteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Workflow">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setWorkflowToDelete(workflow);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Workflow Editor Tab */}
      <TabPanel value={tabValue} index={1}>
        {selectedWorkflow ? (
          <div style={{ height: 'calc(100vh - 200px)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Typography variant="h5">
                {isEditing ? 'Edit' : 'View'} Workflow: {selectedWorkflow.name}
              </Typography>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant={isEditing ? 'contained' : 'outlined'}
                  onClick={() => setIsEditing(!isEditing)}
                  startIcon={isEditing ? <ViewIcon /> : <EditIcon />}
                >
                  {isEditing ? 'View Mode' : 'Edit Mode'}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ExecuteIcon />}
                  onClick={() => handleExecuteWorkflow(selectedWorkflow)}
                >
                  Execute
                </Button>
              </div>
            </div>

            <WorkflowCanvas
              definition={selectedWorkflow.definition}
              onChange={handleSaveWorkflow}
              onValidate={handleValidateWorkflow}
              onNodeSelect={handleNodeSelect}
              onEdgeCreate={handleSaveWorkflow}
              selectedNodeId={selectedNodeId || undefined}
              readOnly={!isEditing}
            />
          </div>
        ) : (
          <Paper style={{ padding: 32, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Select a workflow to edit
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Execution Monitor Tab */}
      <TabPanel value={tabValue} index={2}>
        <Typography variant="h5" gutterBottom>
          Execution Monitor
        </Typography>
        <Paper style={{ padding: 32, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Execution monitoring coming soon...
          </Typography>
        </Paper>
      </TabPanel>

      {/* Create Workflow Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Workflow</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Workflow Name"
            fullWidth
            variant="outlined"
            value={newWorkflowName}
            onChange={(e) => setNewWorkflowName(e.target.value)}
            style={{ marginBottom: 16 }}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newWorkflowDescription}
            onChange={(e) => setNewWorkflowDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateWorkflow}
            variant="contained"
            disabled={!newWorkflowName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Workflow</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{workflowToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteWorkflow} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* Node Configuration Dialog */}
      <NodeConfigDialog
        open={nodeConfigDialogOpen}
        node={selectedNode}
        onClose={handleNodeConfigClose}
        onSave={handleNodeConfigSave}
      />
    </Container>
  );
};

export default WorkflowsPage;